import { useChannel } from 'ably/react';
import { useState } from 'react';
import { CommentType, FeedNames, PostType } from '../memeverse_types';
import { useAuth } from '../../../hooks/useAuth';
import { useQueryClient } from '@tanstack/react-query';
import {
  addNewCommentInQueryData,
  addNewPostToQueryData,
  deleteCommentInQueryData,
  deletePostFromQueryData,
  editLikeCommentInQueryData,
  editLikePostInQueryData,
  editThinkingCommentInQueryData,
  updateCommentInQueryData,
  updatePostInQueryData
} from '../helper';

export const useAskWebsockets = (
  queryKey: FeedNames,
  jumpToFunc: () => void = null
) => {
    // give a way for this hook to refresh frontend
    const [refresh, setRefresh] = useState<boolean>(false);
    const { authUser } = useAuth();
    const queryClient = useQueryClient();

    useChannel('ask', (message) => {
        let hasMutatedData = false;
        try {
          if (message.name.startsWith('post.new.')) {
            const docId = message.name.split('.')[2];
            // new post
            let newPost: PostType = message.data;
            newPost.updatedAt ||= newPost.createdAt;
            newPost.docId ||= docId;
            newPost.comments_list ||= [];
            newPost.comments_counts ||= 0;
            newPost.flat_comments_list ||= [];

            if (message.data.images &&
              message.data.images.length &&
              typeof message.data.images[0] !== 'string') {
              try {
                newPost.images = message.data.images.map((imgData: any) => imgData?.image_url || null);
              } catch (e) {

              }
            }

            hasMutatedData = addNewPostToQueryData(
              newPost,
              queryClient,
              queryKey
            )

            // jump to new post if it was created by the user
            if (newPost.user_id === authUser?.uid && !!jumpToFunc && hasMutatedData) {
              setTimeout(jumpToFunc, 250)
            }
          } else if (message.name.startsWith('post.update.')) {
            let newPost: PostType = message.data;
            newPost.updatedAt = (new Date()).toString();

            hasMutatedData = updatePostInQueryData(
              newPost,
              queryClient,
              queryKey,
            )
          } else if (message.name.startsWith('post.delete.')) {
            let docId = message.name.split('.')[2];

            hasMutatedData = deletePostFromQueryData(
              docId,
              queryClient,
              queryKey
            )
          } else if (message.name.startsWith('comment.new.')) {
            let newCommentData: CommentType = message.data;
            newCommentData.flat_comments_list ||= [];

            hasMutatedData = addNewCommentInQueryData(
              newCommentData,
              queryClient,
              queryKey
            )
          } else if (message.name.startsWith('comment.update.')) {
            let newComment: CommentType = message.data;
            newComment.updatedAt = (new Date()).toString();

            hasMutatedData = updateCommentInQueryData(
              newComment,
              queryClient,
              queryKey
            )

          } else if (message.name.startsWith('comment.delete.')) {
            const commentId = message.name.split('.')[2];

            hasMutatedData = deleteCommentInQueryData(
              commentId,
              queryClient,
              queryKey
            )
          } else if (message.name.startsWith('like.new.post.') ||
            message.name.startsWith('like.delete.post.')) {
            let docId = message.name.split('.')[3];
            let shouldAdd = message.name.split('.')[1] === 'new';
            let user_id = message.data?.user_id;

            hasMutatedData = editLikePostInQueryData(
              docId,
              shouldAdd,
              queryClient,
              queryKey,
              authUser?.uid && authUser?.uid === user_id,
            )
          } else if (message.name.startsWith('like.new.comment.') ||
            message.name.startsWith('like.delete.comment.')) {
            let docId = message.name.split('.')[3];
            let shouldAdd = message.name.split('.')[1] === 'new';
            let user_id = message.data?.user_id;

            hasMutatedData = editLikeCommentInQueryData(
              docId,
              shouldAdd,
              queryClient,
              queryKey,
              authUser?.uid && authUser?.uid === user_id,
            )
          } else if (message.name.startsWith('comment.thinking.')) {
            if (message.data.userId !== authUser?.uid) {
              // only handle this for other users thinking
              let removing = message.name.split('.')[2] !== 'new';

              hasMutatedData = editThinkingCommentInQueryData(
                message.data.userId,
                message.data,
                removing,
                queryClient,
                queryKey
              )
            }
          }

          } catch (err) {
            console.log('Error: ', err)
          } finally {
            // refresh the page to ensure that the changes appear
            if (hasMutatedData) {
              setTimeout(() => {
                setRefresh(!refresh)
              }, 100)
            }
          }
        }
      )

    return { refresh };
}
